import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ConfirmContext = createContext();

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null); // { title, message, confirmLabel, danger }
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({
      title: opts.title || 'Are you sure?',
      message: opts.message || '',
      confirmLabel: opts.confirmLabel || 'Delete',
      cancelLabel: opts.cancelLabel || 'Cancel',
      danger: opts.danger !== false,
    });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const close = useCallback((result) => {
    setState(null);
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
  }, []);

  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [state, close]);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <div className="modal-overlay" onClick={() => close(false)}>
          <div className="modal modal-confirm" onClick={(e) => e.stopPropagation()}>
            <h2>{state.title}</h2>
            {state.message && <p className="modal-confirm-message">{state.message}</p>}
            <div className="modal-confirm-actions">
              <button className="btn btn-secondary" onClick={() => close(false)} type="button">
                {state.cancelLabel}
              </button>
              <button
                className={`btn ${state.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => close(true)}
                type="button"
                autoFocus
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  // Fallback to native confirm if provider missing
  return ctx || { confirm: ({ message } = {}) => Promise.resolve(window.confirm(message || 'Are you sure?')) };
};
